/**
 * @fileoverview Step 1 (basic information) UI for the Register page.
 * @module pages/register/RegisterStep1
 */
import CountryCodeSelector from "../../components/CountryCodeSelector/index.jsx";
import { getSelectedCountryCodeOption, validateEmail } from "../../utils/common.js";
import { selectStyles } from "../../utils/common.js";
import { ReactComponent as VisibilityOnIcon } from "../../assets/icons/Register/VisibilityOnIcon.svg";
import { ReactComponent as VisibilityOffIcon } from "../../assets/icons/Register/VisibilityOffIcon.svg";
import styles from "../../styles/Register.module.css";

const RegisterStep1 = ({
  t,
  values,
  countryCodeOptions,
  passwordValidation,
  handleNamedInputChange,
  handleCountryCodeChange,
  handlePhoneChange,
  handleClickShowPassword,
  handleClickShowConfirmPassword,
}) => {
  const emailFormatInvalid =
    values.correoElectronicoPrincipal.trim() !== "" &&
    !validateEmail(values.correoElectronicoPrincipal).isValid;

  return (
  <div className={styles.formStep1}>
    <div className={styles.formStepTitle}>
      1. {t("pages.register.step1Title1")}{" "}
      <span className={styles.formStepTitle2}>
        {t("pages.register.step1Title2")}
      </span>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.firstNameLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="firstName"
            value={values.firstName}
            onChange={handleNamedInputChange}
            className={styles.formNotSingleInput}
            placeholder={t("pages.register.firstNamePlaceholder")}
          />
        </div>
      </div>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.lastNameLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="lastName"
            value={values.lastName}
            onChange={handleNamedInputChange}
            className={styles.formNotSingleInput}
            placeholder={t("pages.register.lastNamePlaceholder")}
          />
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.phoneLabel")}
        </div>
        <div className={styles.phoneInputContainer}>
          <CountryCodeSelector
            value={getSelectedCountryCodeOption(
              countryCodeOptions,
              values.codigoPais,
            )}
            onChange={handleCountryCodeChange}
            styles={selectStyles}
            placeholder={t("pages.register.countryCodePlaceholder")}
          />
          <input
            type="text"
            value={values.telefonoDeOficina}
            onChange={handlePhoneChange}
            className={styles.phoneInput}
            placeholder={t("pages.register.phonePlaceholder")}
          />
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.emailLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="correoElectronicoPrincipal"
            value={values.correoElectronicoPrincipal}
            onChange={handleNamedInputChange}
            className={`${styles.formSingleInput} ${
              emailFormatInvalid ? styles.inputError : ""
            }`}
            placeholder={t("pages.register.emailPlaceholder")}
          />
          {emailFormatInvalid && (
            <p className={styles.errorMessage}>
              {t("errors.validation.INVALID_EMAIL")}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.passwordLabel1")}
        </div>
        <div className={styles.passwordInputContainer}>
          <input
            type={values.showPassword ? "text" : "password"}
            name="password"
            value={values.password}
            className={styles.passwordInput}
            onChange={handleNamedInputChange}
            placeholder={t("pages.register.passwordPlaceholder")}
          />
          <button
            type="button"
            className={styles.visibilityButton}
            onClick={handleClickShowPassword}
          >
            {values.showPassword ? (
              <VisibilityOffIcon className={styles.visibilityIcon} />
            ) : (
              <VisibilityOnIcon className={styles.visibilityIcon} />
            )}
          </button>
        </div>
      </div>
    </div>

    <div className={styles.passwordInstructions}>
      <div className={styles.passwordInstructionsTitle}>
        {t("pages.register.passwordInstruction1")}:
      </div>
      <div className={styles.passwordInstructionsList}>
        <div
          className={`${styles.passwordInstructionItem} ${
            !passwordValidation.errors.length ? styles.passwordSuccess : ""
          }`}
        >
          • {t("pages.register.passwordInstruction2")}
        </div>
        <div
          className={`${styles.passwordInstructionItem} ${
            !passwordValidation.errors.uppercase ? styles.passwordSuccess : ""
          }`}
        >
          • {t("pages.register.passwordInstruction3")}
        </div>
        <div
          className={`${styles.passwordInstructionItem} ${
            !passwordValidation.errors.number ? styles.passwordSuccess : ""
          }`}
        >
          • {t("pages.register.passwordInstruction4")}
        </div>
        <div
          className={`${styles.passwordInstructionItem} ${
            !passwordValidation.errors.match ? styles.passwordSuccess : ""
          }`}
        >
          • {t("pages.register.passwordInstruction5")}
        </div>
      </div>
    </div>

    <div className={`${styles.formRow} ${styles.specialPaddingTop}`}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.passwordLabel2")}
        </div>
        <div className={styles.passwordInputContainer}>
          <input
            type={values.showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={values.confirmPassword}
            className={styles.passwordInput}
            onChange={handleNamedInputChange}
            placeholder={t("pages.register.passwordPlaceholder2")}
          />
          <button
            type="button"
            className={styles.visibilityButton}
            onClick={handleClickShowConfirmPassword}
          >
            {values.showConfirmPassword ? (
              <VisibilityOffIcon className={styles.visibilityIcon} />
            ) : (
              <VisibilityOnIcon className={styles.visibilityIcon} />
            )}
          </button>
        </div>
      </div>
    </div>

    <p className={styles.formInstructions}>
      *{t("pages.register.allFieldsRequired")}
    </p>
  </div>
  );
};

export default RegisterStep1;
